'use client';

import { Check, CircleOff, Lock, Search, UserRound } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { Button } from './button';
import { Input } from './input';
import { cn } from '../utils/cn';

export type AssigneeRosterPerson = {
  userId: string;
  label: string;
  initials: string;
  photoUrl?: string;
  isCurrentUser?: boolean;
  isFormerMember?: boolean;
  assignedCount?: number;
};

export type AssigneeRosterPanelLabels = {
  searchPlaceholder: string;
  emptyMessage: string;
  nobody: string;
  youBadge: string;
  formerMember: string;
  shareConfirmTitle: string;
  shareConfirmBody: string;
  shareAndAssign: string;
  cancel: string;
  back: string;
  loadError: string;
  bulkTitle?: string;
  bulkCount?: string;
  bulkPrivateNote?: string;
  bulkPrivateNotePlural?: string;
};

export type AssigneeRosterPanelProps = {
  labels: AssigneeRosterPanelLabels;
  people: AssigneeRosterPerson[];
  /** Checked person id, or null when Niemand / unassigned is selected. */
  selectedUserId: string | null;
  search: string;
  onSearchChange: (value: string) => void;
  isLoading?: boolean;
  isError?: boolean;
  pendingShare?: AssigneeRosterPerson | null;
  onCancelShare?: () => void;
  onConfirmShare?: () => void;
  onSelectPerson: (person: AssigneeRosterPerson) => void;
  onSelectNobody: () => void;
  hideNobody?: boolean;
  selectedCount?: number;
  privateShareCount?: number;
  confirmLayout?: 'popover' | 'sheet';
  className?: string;
};

function AssigneeAvatar({
  label,
  initials,
  photoUrl,
  former,
  sizeClass = 'size-8',
}: {
  label: string;
  initials: string;
  photoUrl?: string;
  former?: boolean;
  sizeClass?: string;
}) {
  return (
    <Avatar className={cn(sizeClass, 'shrink-0')}>
      {photoUrl && !former ? <AvatarImage src={photoUrl} alt={label} /> : null}
      <AvatarFallback
        className={cn(
          'text-[10px] font-semibold',
          former
            ? 'bg-muted text-muted-foreground'
            : 'bg-primary/10 text-primary',
        )}
      >
        {former ? <UserRound className="size-3.5" aria-hidden /> : initials}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Presentational assignee / person roster: search, list, Niemand, former-member
 * rows, and private share+assign confirm. Domain wiring stays in the app.
 */
export function AssigneeRosterPanel({
  labels,
  people,
  selectedUserId,
  search,
  onSearchChange,
  isLoading,
  isError,
  pendingShare,
  onCancelShare,
  onConfirmShare,
  onSelectPerson,
  onSelectNobody,
  hideNobody = false,
  selectedCount = 0,
  privateShareCount = 0,
  confirmLayout = 'popover',
  className,
}: AssigneeRosterPanelProps) {
  if (pendingShare) {
    const title = labels.shareConfirmTitle.replace(
      '{name}',
      pendingShare.label,
    );
    const body = labels.shareConfirmBody.replaceAll(
      '{name}',
      pendingShare.label,
    );
    if (confirmLayout === 'sheet') {
      return (
        <div
          className={cn(
            'flex flex-col items-center gap-4 px-4 pb-6 pt-2',
            className,
          )}
        >
          <div className="flex size-14 items-center justify-center rounded-full bg-muted">
            <Lock className="size-6 text-muted-foreground" aria-hidden />
          </div>
          <div className="space-y-2 text-center">
            <p className="text-[17px] font-semibold text-foreground">
              {title}?
            </p>
            <p className="text-[13.5px] leading-relaxed text-muted-foreground text-pretty">
              {body}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2">
            <Button type="button" className="w-full" onClick={onConfirmShare}>
              {labels.shareAndAssign}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onCancelShare}
            >
              {labels.back}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className={cn('flex flex-col gap-3 p-3', className)}>
        <div className="flex items-start gap-2">
          <Lock
            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <div className="min-w-0 space-y-1.5">
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <p className="text-[12.5px] leading-relaxed text-muted-foreground text-pretty">
              {body}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancelShare}
          >
            {labels.cancel}
          </Button>
          <Button type="button" size="sm" onClick={onConfirmShare}>
            {labels.shareAndAssign}
          </Button>
        </div>
      </div>
    );
  }

  const privateNote =
    privateShareCount > 0
      ? (privateShareCount === 1
          ? labels.bulkPrivateNote
          : (labels.bulkPrivateNotePlural ?? labels.bulkPrivateNote)
        )?.replace('{count}', String(privateShareCount))
      : null;

  return (
    <div className={cn('flex flex-col', className)}>
      {selectedCount > 0 && labels.bulkTitle ? (
        <p className="border-b border-border/60 px-3 py-2 text-[12.5px] font-medium text-muted-foreground">
          {labels.bulkTitle.replace('{count}', String(selectedCount))}
        </p>
      ) : null}
      <div className="p-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={labels.searchPlaceholder}
            className="h-9 pl-8"
          />
        </div>
      </div>
      {isError ? (
        <p className="px-3 py-6 text-center text-sm text-destructive">
          {labels.loadError}
        </p>
      ) : null}
      {!isError && isLoading && people.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-muted-foreground">…</p>
      ) : null}
      {!isError && !isLoading && people.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-muted-foreground">
          {labels.emptyMessage}
        </p>
      ) : null}
      <div className="max-h-64 overflow-y-auto px-1 pb-1">
        {people.map((person) => {
          const selected = selectedUserId === person.userId;
          const disabled = person.isFormerMember === true && !selected;
          const countLabel =
            typeof person.assignedCount === 'number' &&
            selectedCount > 0 &&
            labels.bulkCount
              ? labels.bulkCount
                  .replace('{assigned}', String(person.assignedCount))
                  .replace('{total}', String(selectedCount))
              : null;
          return (
            <button
              key={person.userId}
              type="button"
              disabled={disabled}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm',
                disabled
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:bg-muted/80',
                selected && 'bg-muted/60',
              )}
              onClick={() => {
                if (disabled || selected) return;
                onSelectPerson(person);
              }}
            >
              <AssigneeAvatar
                label={person.label}
                initials={person.initials}
                photoUrl={person.photoUrl}
                former={person.isFormerMember}
              />
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate font-medium text-foreground">
                    {person.label}
                  </span>
                  {person.isCurrentUser ? (
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {labels.youBadge}
                    </span>
                  ) : null}
                </span>
                {person.isFormerMember ? (
                  <span className="block truncate text-[11.5px] text-muted-foreground">
                    {labels.formerMember}
                  </span>
                ) : null}
                {countLabel ? (
                  <span className="block truncate text-[11.5px] text-muted-foreground">
                    {countLabel}
                  </span>
                ) : null}
              </span>
              {selected ? (
                <Check className="size-4 shrink-0 text-primary" aria-hidden />
              ) : null}
            </button>
          );
        })}
        {!hideNobody ? (
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm hover:bg-muted/80',
              selectedUserId === null && 'bg-muted/60',
            )}
            onClick={() => {
              if (selectedUserId === null) return;
              onSelectNobody();
            }}
          >
            <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/40 text-muted-foreground">
              <CircleOff className="size-3.5" aria-hidden />
            </span>
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {labels.nobody}
            </span>
            {selectedUserId === null ? (
              <Check className="size-4 shrink-0 text-primary" aria-hidden />
            ) : null}
          </button>
        ) : null}
      </div>
      {privateNote ? (
        <p className="border-t border-border/60 px-3 py-2 text-[11.5px] leading-snug text-muted-foreground text-pretty">
          {privateNote}
        </p>
      ) : null}
    </div>
  );
}

export { AssigneeAvatar };
